import { FlagOutlined, PlusCircleOutlined, SaveOutlined, GroupOutlined, InfoOutlined, CopyOutlined, RetweetOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, message, Modal, Row, Tooltip } from 'antd';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import MainContent from '@/layouts/MainContent';
import { useTranslation } from 'react-i18next';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED, dateTimeDisplayFormat } from '@/config';
import BooleanTag from '@/components/app/BooleanTag';
import BaseTicketClassInput from '@/components/FormItems/BaseTicketClassInput';
import ColorPicker from '@/components/FormItems/ColorPicker';
import RouteInput from '@/components/FormItems/RouteInput';
import ExportColumnsSelectorModal from '@/components/modals/ExportColumnsSelectorModal';
import { exportService } from '@/services/exportService';
import ExportBtn from '@/components/app/ExportBtn';
import moment from 'moment';
import TripGroupsModal from './TripGroupsModal';
import CreateTripModal from './CreateTripModal';
import BaseTripDetailModal from './BaseTripDetailModal';
import CopyTripModal from './CopyTripModal';
import { clearAllBaseTripsCache } from '@/hooks/data/masterDataHook';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import { TripDto } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { useCanManageFleetTrip } from '@/hooks/useHasPermissions';
import { RouteAndTripIcon } from '@/assets/icons/entities/_';
import BaseTripMapEditorContainer from '@/components/map/TripMap/BaseTripMapEditorContainer';

const PAGE_TITLE = 'tripsPage_Title';

const columns: IColumnType<TripDto>[] = [
  {
    title: 'tripsPage_TripName',
    dataIndex: 'tripName',
    ellipsis: true,
    sorter: true,
    width: 200,
    render: v => <b>{v}</b>,
  },
  {
    title: 'tripsPage_Route',
    dataIndex: 'routeName',
    ellipsis: true,
    width: 200,
  },
  {
    title: 'tripsPage_Source',
    dataIndex: 'sourceName',
    ellipsis: true,
  },
  {
    title: 'tripsPage_DepartureDateTime',
    dataIndex: 'plannedDepartureDateTime',
    ellipsis: true,
    width: 160,
    render: v => dateTimeDisplayFormat.originDateTime(v),
  },
  {
    title: 'tripsPage_Destination',
    dataIndex: 'destinationName',
    ellipsis: true,
  },
  {
    title: 'tripsPage_ArrivalDateTime',
    dataIndex: 'plannedArrivalDateTime',
    ellipsis: true,
    render: v => dateTimeDisplayFormat.originDateTime(v),
    width: 160,
  },
  {
    title: 'tripsPage_Duration',
    dataIndex: 'id',
    ellipsis: true,
    align: 'center',
    width: 140,
    render: (v, trip) => {
      if (moment(trip.plannedArrivalDateTime) < moment(trip.plannedDepartureDateTime)) {
        return 'N/A';
      }
      const diff = moment(trip.plannedArrivalDateTime)
        .set('second', 0)
        .set('millisecond', 0)
        .diff(
          moment(trip.plannedDepartureDateTime)
            .set('second', 0)
            .set('millisecond', 0),
        );
      const h = 60 * 60 * 1000;
      const hours = Math.floor(diff / h);
      const minutes = Math.floor((diff - hours * h) / (60 * 1000));
      return `${hours < 10 ? '0' + hours : hours}:${minutes < 10 ? '0' + minutes : minutes}`;
    },
  },
  {
    title: 'tripsPage_NoOfStops',
    dataIndex: 'noOfStops',
    ellipsis: true,
    render: v => <b>{v}</b>,
    align: 'center',
  },
  {
    title: 'tripsPage_IsRecurred',
    dataIndex: 'isRecurred',
    ellipsis: true,
    render: v => <BooleanTag value={v} />,
    align: 'center',
  },
  {
    title: 'base.Active',
    dataIndex: 'isActive',
    ellipsis: true,
    render: v => <BooleanTag value={v} />,
    align: 'center',
  },
];

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'searchText', label: 'tripsPage_SearchText' },
  { type: 'SELECT', name: 'routeId', label: 'tripsPage_Route', customElm: <RouteInput /> },
  { type: 'DATE', name: 'departureDate', label: 'tripsPage_DepartureDate' },
];

const TripsPage = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportColumns, setExportColumns] = React.useState<string[]>([]);
  const [showExportModal, toggleExportModal] = React.useState<boolean>(false);
  const [displayTripGroupModal, setDisplayTripGroupModal] = React.useState<boolean>(false);
  const [displayCreateTripModal, setDisplayCreateTripModal] = React.useState<boolean>(false);
  const [tripDetailId, setTripDetailId] = React.useState<string>();
  const [copyTripId, setCopyTripId] = React.useState<string>();
  const [baseTripMapId, setBaseTripMapId] = React.useState<string>();
  const canUpdate = useCanManageFleetTrip();

  const clearCache = () => {
    clearAllBaseTripsCache();
  };

  React.useEffect(() => {
    apiSdk.TripService.getExportColumns().then(res => {
      setExportColumns(res);
    });
  }, []);
  const createEntity = values => {
    setLoading(true);
    apiSdk.CompanyTicketClassService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          clearCache();
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.CompanyTicketClassService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          clearCache();
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onExportExcel = (cols: string[]) => {
    exportService
      .exportTrips({
        ...zTb.current?.getFilter(),
        columns: cols,
      })
      .toExcel();
  };
  const onExportPdf = (cols: string[]) => {
    exportService
      .exportTrips({
        ...zTb.current?.getFilter(),
        columns: cols,
      })
      .toPdf();
  };

  const activeOrInActiveTrip = (tripId: string, status: boolean) => {
    Modal.confirm({
      title: t('Confirm'),
      content: status ? 'Are you sure active this trip?' : 'Are you sure in active this trip?',
      onOk: () => {
        setLoading(true);
        apiSdk.TripService.considerSearch({
          allowSearch: status,
          id: tripId,
        })
          .then(res => {
            if (res.success) {
              zTb.current?.reload();
            } else {
              defaultApiErrorHandle(res);
            }
          })
          .catch(defaultApiErrorHandle)
          .finally(() => {
            setLoading(false);
          });
      },
    });
  };
  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={
        canUpdate
          ? [
              <Button
                key="create"
                type="primary"
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  setDisplayCreateTripModal(true);
                }}
              >
                {t('tripsPage_CreateTrip')}
              </Button>,
              <Button
                key="tripgroup"
                type="default"
                icon={<GroupOutlined />}
                onClick={() => {
                  setDisplayTripGroupModal(true);
                }}
              >
                {t('tripsPage_TripGroups')}
              </Button>,
            ]
          : null
      }
      icon={<FlagOutlined style={{ fontSize: 16 }} />}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/fleets/trips/get-trips"
          bordered
          order_by="createdOn"
          order_by_asc={true}
          allowEdit={false}
          onEdit={record => {
            setIsNewForm(false);
            setFormVisible(true);
            form.setFieldsValue({
              ...record,
            });
          }}
          actionColumnWidth={150}
          allowDelete={false}
          filters={filters}
          extraActions={[
            {
              render: record => {
                return canUpdate ? (
                  <Tooltip title={t('tripsPage_ActiveOrInActive')}>
                    <Button
                      size="small"
                      onClick={() => activeOrInActiveTrip(record.id, !record.isActive)}
                      danger={record.isActive}
                      type="primary"
                      icon={<RetweetOutlined />}
                    />
                  </Tooltip>
                ) : null;
              },
            },
            {
              render: record => {
                return canUpdate ? (
                  <Tooltip title={t('tripsPage_Map')}>
                    <Button
                      size="small"
                      onClick={() => setBaseTripMapId(record.id)}
                      type="default"
                      icon={<RouteAndTripIcon style={{ width: 16, height: 16 }} />}
                    />
                  </Tooltip>
                ) : null;
              },
            },
            {
              render: record => {
                return canUpdate ? (
                  <Tooltip title={t('tripsPage_CopyTrip')}>
                    <Button size="small" onClick={() => setCopyTripId(record.id)} danger type="default" icon={<CopyOutlined />} />
                  </Tooltip>
                ) : null;
              },
            },

            {
              render: record => {
                return (
                  <Tooltip title={t('tripsPage_TripDetail')}>
                    <Button size="small" onClick={() => setTripDetailId(record.id)} type="primary" icon={<InfoOutlined />} />
                  </Tooltip>
                );
              },
            },
          ]}
          extraBtns={
            <ExportBtn
              disabled={!exportColumns?.length}
              onClick={() => {
                toggleExportModal(true);
              }}
            />
          }
          searchBtnAlignLeft
          onDeleteCompleted={clearCache}
        ></ZTable>
        {formVisible ? (
          <Modal
            open={formVisible}
            maskClosable={false}
            width={ModalWidth.small}
            className="nta-custom-form"
            title={isNewForm ? t('ticketClassesPage_CreateTicketClass') : t('ticketClassesPage_UpdateTicketClass')}
            forceRender={true}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  if (values.id) {
                    updateEntity(values);
                  } else {
                    createEntity(values);
                  }
                })
                .catch(info => {});
            }}
          >
            <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
              <Form.Item name="id" className="hidden">
                <Input type="hidden" />
              </Form.Item>

              <Row gutter={ROW_GUTTER}>
                <Col md={24}>
                  <Form.Item
                    label={t('ticketClassesPage_BaseTicketClassName')}
                    name="ticketClassId"
                    rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                  >
                    <BaseTicketClassInput />
                  </Form.Item>
                  <Form.Item label={t('ticketClassesPage_Name')} name="name" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item label={t('ticketClassesPage_SeatColor')} name="seatColor" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                    <ColorPicker />
                  </Form.Item>
                  <Form.Item label="   " colon={false} name="isStandard" valuePropName="checked">
                    <Checkbox>{t('base.IsStandard')} </Checkbox>
                  </Form.Item>
                </Col>
                <Col md={12}></Col>
              </Row>
            </Form>
          </Modal>
        ) : null}
      </div>
      {showExportModal ? (
        <ExportColumnsSelectorModal
          onClose={() => toggleExportModal(false)}
          columns={exportColumns}
          onExportExcel={onExportExcel}
          onExportPdf={onExportPdf}
        />
      ) : null}
      {displayTripGroupModal ? (
        <TripGroupsModal
          onClose={() => {
            setDisplayTripGroupModal(false);
          }}
        />
      ) : null}
      {displayCreateTripModal ? (
        <CreateTripModal
          onClose={reload => {
            setDisplayCreateTripModal(false);
            if (reload) {
              zTb.current?.reload();
            }
          }}
        />
      ) : null}
      {tripDetailId ? (
        <BaseTripDetailModal
          tripId={tripDetailId}
          onClose={() => {
            setTripDetailId(undefined);
          }}
        />
      ) : null}
      {copyTripId ? (
        <CopyTripModal
          tripId={copyTripId}
          onClose={reload => {
            setCopyTripId(undefined);
            if (reload) {
              zTb.current?.reload();
            }
          }}
        />
      ) : null}

      {baseTripMapId ? <BaseTripMapEditorContainer baseTripId={baseTripMapId} onClose={() => setBaseTripMapId(undefined)} /> : null}
    </MainContent>
  );
};
export default TripsPage;
